import { createFileRoute, Outlet, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, Badge } from "@/components/ui/primitives";
import { StageStepper, type StageKey } from "@/components/StageStepper";
import { AutoRunControl } from "@/components/AutoRunControl";
import { api } from "@/lib/api";
import { formatRelative } from "@/lib/utils";
import { AutoRunProvider, PlaybackStageProvider } from "@/lib/playback";

export const Route = createFileRoute("/apps/$id")({
  component: ApplicationShell,
  loader: async ({ params, context }) => {
    await context.queryClient.prefetchQuery({
      queryKey: ["application", params.id],
      queryFn: () => api.getApplication(params.id),
    });
  },
});

function ApplicationShell() {
  const { id } = Route.useParams();
  const routerState = useRouterState();
  const path = routerState.location.pathname;
  const stageFromPath = ((): StageKey => {
    if (path.endsWith("/intake")) return "intake";
    if (path.endsWith("/analysis")) return "analysis";
    if (path.endsWith("/coordination")) return "coordination";
    if (path.endsWith("/decision")) return "decision";
    return "intake";
  })();

  const { data: app } = useQuery({
    queryKey: ["application", id],
    queryFn: () => api.getApplication(id),
  });

  const reachedStage = (app?.stage as StageKey) ?? stageFromPath;
  // The stepper's "active" stage follows the URL, so the user can navigate
  // among stages without the circle jumping around.
  const active = stageFromPath;

  return (
    <AutoRunProvider appId={id}>
      <PlaybackStageProvider>
        <div className="space-y-8">
          {app && (
            <Card className="p-6">
              <div className="flex items-start justify-between gap-6">
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-semibold tracking-tight">{app.customer.full_name}</h1>
                    <Badge variant="muted">{app.product_label}</Badge>
                    {app.risk_tier && <Badge variant="default">Tier {app.risk_tier}</Badge>}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-x-12 gap-y-1 text-sm text-muted-foreground md:grid-cols-4">
                    <Meta label="Amount">{app.amount_display}</Meta>
                    {app.term_months > 0 && <Meta label="Term">{app.term_months} months</Meta>}
                    <Meta label="Purpose">{app.purpose}</Meta>
                    <Meta label="Submitted">{formatRelative(app.submitted_at)}</Meta>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-3 pt-1">
                  <StatusChipBig status={app.status} label={app.status_label} />
                  <AutoRunControl
                    appId={id}
                    stage={reachedStage}
                    status={app.status}
                  />
                </div>
              </div>
            </Card>
          )}

          <Card className="p-8">
            <StageStepper applicationId={id} active={active} reached={reachedStage} />
          </Card>

          <Outlet />
        </div>
      </PlaybackStageProvider>
    </AutoRunProvider>
  );
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[0.72rem] uppercase tracking-wider text-muted-foreground/70">{label}</div>
      <div className="text-foreground">{children}</div>
    </div>
  );
}

function StatusChipBig({ status, label }: { status: string; label: string }) {
  const tone: "success" | "warn" | "destructive" | "info" | "muted" = (() => {
    switch (status) {
      case "auto_approved":
      case "approved_with_discretion":
        return "success";
      case "pending_review":
      case "needs_info":
        return "warn";
      case "fraud_hold":
      case "auto_declined":
      case "declined":
        return "destructive";
      case "in_flight":
        return "info";
      default:
        return "muted";
    }
  })();
  return <Badge variant={tone} className="text-sm px-3 py-1">{label}</Badge>;
}
